import { useEffect, useRef, useCallback, useState, useMemo } from 'react';
import { Users, UserMinus, UserPlus, Paperclip, Download, X, Video, Bot, Smile, Pencil, Check } from 'lucide-react';
import { useChat } from '../../contexts/useChat';
import { authHeaders } from '../../utils/authHeaders';
import { Marked } from 'marked';

const md = new Marked({ breaks: true, gfm: true });

function renderMarkdown(content) {
  const normalized = (content || '').replace(/\n{2,}/g, '\n');
  const html = md.parse(normalized);
  return <div className="ai-markdown" dangerouslySetInnerHTML={{ __html: html }} />;
}

const EMOJI_LIST = ['👍', '❤️', '😂', '😮', '😢', '🔥', '🎉', '👏', '🤔', '✅'];

function getInitials(name = '') {
  return name.split(/\s+/).map(w => w[0]).join('').toUpperCase().slice(0, 2) || '?';
}

function formatTime(dateStr) {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' });
}

function isSameGroup(prev, curr) {
  if (!prev) return false;
  if (prev.type === 'ai' || curr.type === 'ai') return false;
  if (prev.userId !== curr.userId) return false;
  return new Date(curr.createdAt) - new Date(prev.createdAt) < 5 * 60 * 1000;
}

function formatBytes(bytes) {
  if (bytes < 1024) return bytes + 'B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + 'KB';
  return (bytes / 1024 / 1024).toFixed(1) + 'MB';
}

function TypingDots() {
  return (
    <span className="inline-flex gap-0.5 items-end ml-1">
      {[0, 1, 2].map(i => (
        <span key={i} className="w-1 h-1 rounded-full bg-gray-500 animate-bounce" style={{ animationDelay: `${i * 0.15}s` }} />
      ))}
    </span>
  );
}

function Avatar({ user, size = 9 }) {
  const sizeClass = size === 9 ? 'w-9 h-9 text-xs' : size === 7 ? 'w-7 h-7 text-[11px]' : 'w-8 h-8 text-xs';
  if (user?.avatarUrl) {
    return <img src={user.avatarUrl} alt={user.name} className={`${sizeClass} rounded-full object-cover flex-shrink-0`} />;
  }
  return (
    <div className={`${sizeClass} rounded-full bg-brand/20 text-brand font-bold flex items-center justify-center select-none flex-shrink-0`}>
      {getInitials(user?.name)}
    </div>
  );
}

function AIAvatar({ size = 9 }) {
  const dim = size === 9 ? 36 : size === 7 ? 28 : 32;
  const sizeClass = size === 9 ? 'w-9 h-9' : size === 7 ? 'w-7 h-7' : 'w-8 h-8';
  return (
    <div className={`${sizeClass} rounded-full flex-shrink-0 overflow-hidden`} style={{ flexShrink: 0 }}>
      <svg viewBox="0 0 36 36" width={dim} height={dim} fill="none" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient id="ai-grad" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#00B2FF"/>
            <stop offset="100%" stopColor="#7c00b8"/>
          </linearGradient>
        </defs>
        <circle cx="18" cy="18" r="18" fill="url(#ai-grad)"/>
        {/* 안테나 */}
        <line x1="18" y1="3" x2="18" y2="9" stroke="white" strokeWidth="1.5" strokeLinecap="round" opacity="0.85"/>
        <circle cx="18" cy="2.5" r="1.8" fill="white" opacity="0.85"/>
        {/* 헤드 박스 */}
        <rect x="10" y="10" width="16" height="14" rx="4" fill="white" opacity="0.15"/>
        {/* 눈 (LED 스타일) */}
        <rect x="12" y="14" width="4" height="3" rx="1.5" fill="white" opacity="0.95"/>
        <rect x="20" y="14" width="4" height="3" rx="1.5" fill="white" opacity="0.95"/>
        {/* 입 */}
        <path d="M14 20.5 Q18 23 22 20.5" stroke="white" strokeWidth="1.4" fill="none" strokeLinecap="round" opacity="0.9"/>
      </svg>
    </div>
  );
}

// 메시지 텍스트에서 @멘션 하이라이트
function renderText(content, mentionNames = [], myName = '') {
  const parts = content.split(/(@\S+)/g);
  return parts.map((part, i) => {
    if (part.startsWith('@')) {
      const name = part.slice(1);
      if (name === 'AI' || mentionNames.includes(name)) {
        const isMe = name === myName;
        return (
          <span key={i} className={`font-bold rounded px-0.5 ${isMe ? 'bg-white/25 text-white' : 'text-amber-300 bg-amber-500/15'}`}>
            {part}
          </span>
        );
      }
    }
    return <span key={i}>{part}</span>;
  });
}

function ReactionBar({ reactions, myId, onReact }) {
  if (!reactions?.length) return null;
  const grouped = reactions.reduce((acc, r) => {
    if (!acc[r.emoji]) acc[r.emoji] = { count: 0, myReaction: false };
    acc[r.emoji].count++;
    if (r.userId === myId) acc[r.emoji].myReaction = true;
    return acc;
  }, {});
  return (
    <div className="flex flex-wrap gap-1 mt-1">
      {Object.entries(grouped).map(([emoji, { count, myReaction }]) => (
        <button key={emoji} onClick={() => onReact(emoji)}
          className={`flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-xs border transition-all
            ${myReaction ? 'bg-brand/20 border-brand/40 text-brand' : 'bg-gray-800 border-gray-700 text-gray-300 hover:border-gray-500'}`}>
          {emoji} <span className="text-[10px]">{count}</span>
        </button>
      ))}
    </div>
  );
}

function EmojiPickerPopup({ onSelect, onClose, alignRight }) {
  const ref = useRef(null);
  useEffect(() => {
    const handler = e => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);
  return (
    <div ref={ref}
      className={`absolute bottom-full mb-1 z-[80] bg-gray-800 border border-gray-700 rounded-xl p-2 flex gap-1 flex-wrap w-48 ${alignRight ? 'right-0' : 'left-0'}`}>
      {EMOJI_LIST.map(e => (
        <button key={e} onClick={() => { onSelect(e); onClose(); }}
          className="text-lg hover:bg-gray-700 rounded p-1 transition-colors">{e}</button>
      ))}
    </div>
  );
}

function FileMessage({ content, isMine, onImageLoad }) {
  let meta = {};
  try { meta = JSON.parse(content); } catch { return <p className="text-sm text-gray-300">{content}</p>; }
  const RENDERABLE_IMAGE = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml', 'image/bmp'];
  const isImage = RENDERABLE_IMAGE.includes(meta.mime);
  return (
    <div className={`rounded-xl overflow-hidden border max-w-xs ${isMine ? 'border-brand/30' : 'border-gray-700'}`}>
      {isImage ? (
        <a href={meta.url} target="_blank" rel="noopener noreferrer">
          <img
            src={meta.url}
            alt={meta.name}
            onLoad={onImageLoad}
            onError={e => {
              e.target.style.display = 'none';
              e.target.nextSibling && (e.target.nextSibling.style.display = 'flex');
            }}
            className="max-w-[240px] max-h-[200px] object-cover block"
          />
          <div style={{ display: 'none' }} className="w-[240px] h-24 items-center justify-center gap-2 bg-gray-800 flex-col">
            <Download size={20} className="text-gray-500" />
            <span className="text-xs text-gray-500 truncate max-w-[200px] px-2">{meta.name}</span>
          </div>
        </a>
      ) : (
        <a href={meta.url} download={meta.name} target="_blank" rel="noopener noreferrer"
          className={`flex items-center gap-2.5 px-3 py-2.5 hover:opacity-80 transition-opacity ${isMine ? 'bg-brand/10' : 'bg-gray-800'}`}>
          <Download size={16} className={isMine ? 'text-brand' : 'text-gray-400'} />
          <div className="min-w-0">
            <p className="text-sm text-white truncate max-w-[160px]">{meta.name}</p>
            <p className="text-[10px] text-gray-500">{formatBytes(meta.size)}</p>
          </div>
        </a>
      )}
    </div>
  );
}

function AdminPicker({ allAdmins, selected, onChange, excludeIds = [] }) {
  const available = allAdmins.filter(a => !excludeIds.includes(a.id));
  return (
    <div className="border border-gray-700 rounded-lg overflow-hidden max-h-40 overflow-y-auto">
      {available.length === 0 && <p className="text-xs text-gray-500 px-3 py-2">추가할 직원이 없습니다</p>}
      {available.map(a => {
        const checked = selected.includes(a.id);
        return (
          <label key={a.id} className={`flex items-center gap-2 px-3 py-2 cursor-pointer transition-colors ${checked ? 'bg-brand/10' : 'hover:bg-gray-800'}`}>
            <input type="checkbox" className="accent-brand" checked={checked} onChange={e => {
              onChange(e.target.checked ? [...selected, a.id] : selected.filter(id => id !== a.id));
            }} />
            <span className="text-sm text-white">{a.name}</span>
            {a.department && <span className="text-xs text-gray-500">{a.department}</span>}
          </label>
        );
      })}
    </div>
  );
}

function MembersPanel({ channel, me, onClose, onMembersChanged }) {
  const [members, setMembers] = useState([]);
  const [allAdmins, setAllAdmins] = useState([]);
  const [addIds, setAddIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const canManage = me?.role === 'superadmin' || channel.createdBy === me?.id;

  const loadMembers = useCallback(async () => {
    const r = await fetch(`/api/chat/channels/${channel.id}/members`, { headers: authHeaders() });
    const data = await r.json();
    setMembers(Array.isArray(data) ? data : []);
    setLoading(false);
  }, [channel.id]);

  useEffect(() => {
    loadMembers();
    if (canManage) {
      fetch('/api/admins/dropdown', { headers: authHeaders() })
        .then(r => r.json()).then(data => setAllAdmins(Array.isArray(data) ? data : [])).catch(() => {});
    }
  }, [loadMembers, canManage]);

  const handleAdd = async () => {
    if (!addIds.length) return;
    await fetch(`/api/chat/channels/${channel.id}/members`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ userIds: addIds }) });
    setAddIds([]);
    await loadMembers();
    onMembersChanged?.();
  };

  const handleRemove = async userId => {
    if (!confirm('이 멤버를 채널에서 내보내시겠습니까?')) return;
    await fetch(`/api/chat/channels/${channel.id}/members/${userId}`, { method: 'DELETE', headers: authHeaders() });
    await loadMembers();
    onMembersChanged?.();
  };

  const memberIds = members.map(m => m.id);
  const addable = allAdmins.filter(a => !memberIds.includes(a.id));

  return (
    <div className="absolute inset-0 bg-gray-900 z-10 flex flex-col">
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-800 flex-shrink-0">
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 text-brand" />
          <span className="text-sm font-semibold text-white">멤버 관리</span>
          <span className="text-xs text-gray-500">#{channel.name}</span>
        </div>
        <button onClick={onClose} className="text-gray-500 hover:text-white"><X className="w-4 h-4" /></button>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        <div>
          <p className="text-xs text-gray-500 font-medium uppercase tracking-wider mb-2">현재 멤버 ({members.length}명)</p>
          {loading ? <div className="w-4 h-4 border-2 border-brand/30 border-t-brand rounded-full animate-spin" /> : (
            <div className="space-y-1">
              {members.map(m => (
                <div key={m.id} className="flex items-center justify-between px-3 py-2 rounded-lg bg-gray-800/50">
                  <div className="flex items-center gap-2">
                    <Avatar user={m} size={7} />
                    <div>
                      <p className="text-sm text-white">{m.name}</p>
                      {m.department && <p className="text-[10px] text-gray-500">{m.department}</p>}
                    </div>
                    {m.id === channel.createdBy && <span className="text-[9px] bg-brand/20 text-brand px-1.5 py-0.5 rounded">생성자</span>}
                  </div>
                  {canManage && m.id !== channel.createdBy && m.id !== me?.id && (
                    <button onClick={() => handleRemove(m.id)} className="text-gray-600 hover:text-red-400 transition-colors">
                      <UserMinus className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
        {canManage && addable.length > 0 && (
          <div>
            <p className="text-xs text-gray-500 font-medium uppercase tracking-wider mb-2">멤버 추가</p>
            <AdminPicker allAdmins={addable} selected={addIds} onChange={setAddIds} excludeIds={memberIds} />
            {addIds.length > 0 && (
              <button onClick={handleAdd} className="mt-2 w-full flex items-center justify-center gap-1.5 py-2 bg-brand hover:bg-brand/80 text-white text-xs rounded-lg transition-colors">
                <UserPlus className="w-3.5 h-3.5" /> {addIds.length}명 추가
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// 인라인 메시지 편집 컴포넌트
function InlineEdit({ content, onSave, onCancel }) {
  const [text, setText] = useState(content);
  const ref = useRef(null);
  useEffect(() => { ref.current?.focus(); }, []);
  const submit = () => { if (text.trim() && text !== content) onSave(text.trim()); else onCancel(); };
  return (
    <div className="flex flex-col gap-1 w-full">
      <textarea
        ref={ref}
        value={text}
        onChange={e => setText(e.target.value)}
        onKeyDown={e => {
          if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) { e.preventDefault(); submit(); }
          if (e.key === 'Escape') onCancel();
        }}
        className="w-full bg-gray-700 border border-brand/50 rounded-lg px-3 py-2 text-sm text-white resize-none focus:outline-none focus:border-brand min-h-[2rem] max-h-32 overflow-y-auto"
        rows={1}
      />
      <div className="flex gap-1.5 text-[10px]">
        <span className="text-gray-500">Enter 저장 · Esc 취소</span>
        <button onClick={submit} className="ml-auto flex items-center gap-1 text-green-400 hover:text-green-300">
          <Check size={10} /> 저장
        </button>
        <button onClick={onCancel} className="text-gray-400 hover:text-white">취소</button>
      </div>
    </div>
  );
}

export default function ChatMessages() {
  const {
    me, activeChannel, messages, typingUsers, loading,
    newMsg, uploading, showMembers, setShowMembers,
    handleInputChange, sendMessage, handleFileSelect,
    loadChannels, setMeetingChannel, setMeetingOpen,
    isInitialLoadRef, sendReaction, editMessage, setNewMsg,
    readMap, channelMemberCount,
  } = useChat();

  const scrollContainerRef = useRef(null);
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);

  // 채널 멤버 (멘션용)
  const [members, setMembers] = useState([]);
  // @멘션 자동완성
  const [mentionQuery, setMentionQuery] = useState(null);
  const [mentionIdx, setMentionIdx] = useState(0);
  // 메시지 편집
  const [editingId, setEditingId] = useState(null);
  // 이모지 피커
  const [emojiPickerMsgId, setEmojiPickerMsgId] = useState(null);
  // 호버 중인 메시지
  const [hoverMsgId, setHoverMsgId] = useState(null);

  // 채널 변경 시 멤버 로드
  useEffect(() => {
    if (!activeChannel) return;
    setMembers([]);
    fetch(`/api/chat/channels/${activeChannel.id}/members`, { headers: authHeaders() })
      .then(r => r.json())
      .then(data => setMembers(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, [activeChannel?.id]);

  // @멘션 후보 목록
  const AI_ENTRY = { id: '__ai__', name: 'AI', displayName: '프린만 AI', isAI: true };
  const mentionMatches = useMemo(() => {
    if (mentionQuery === null) return [];
    const q = mentionQuery.toLowerCase();
    const aiMatch = !q || 'ai'.startsWith(q) || 'ai 어시스턴트'.includes(q);
    const memberMatches = members.filter(m => !q || m.name.toLowerCase().includes(q));
    return [...(aiMatch ? [AI_ENTRY] : []), ...memberMatches].slice(0, 8);
  }, [mentionQuery, members]);

  // 멘션 삽입
  const insertMention = useCallback((entry) => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    const cursorPos = textarea.selectionStart;
    const text = newMsg;
    const textBefore = text.slice(0, cursorPos);
    const match = textBefore.match(/@(\S*)$/);
    if (!match) return;
    const startOfMention = cursorPos - match[0].length;
    const insertName = entry.isAI ? 'AI' : entry.name;
    const newText = text.slice(0, startOfMention) + '@' + insertName + ' ' + text.slice(cursorPos);
    setNewMsg(newText);
    setMentionQuery(null);
    setTimeout(() => {
      const newCursor = startOfMention + insertName.length + 2;
      textarea.focus();
      textarea.setSelectionRange(newCursor, newCursor);
    }, 0);
  }, [newMsg, setNewMsg]);

  // readMap이 도착한 직후 한 번 더 스크롤이 필요한지 추적
  const justScrolledRef = useRef(false);

  // 스크롤 — messages/typingUsers/loading/readMap 변경 시 실행
  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el || loading) return;  // loading 중엔 DOM에 메시지가 없으므로 대기
    if (isInitialLoadRef.current) {
      el.scrollTop = el.scrollHeight;
      isInitialLoadRef.current = false;
      justScrolledRef.current = true;  // readMap 도착 후 한 번 더 스크롤 예약
    } else if (justScrolledRef.current) {
      // readMap/memberCount 반영 후 배지가 추가되면 scrollHeight가 늘어남 → 다시 맨 밑으로
      el.scrollTop = el.scrollHeight;
      justScrolledRef.current = false;
    } else {
      const lastMsg = messages[messages.length - 1];
      const isAiResponse = lastMsg?.type === 'ai';
      const isAtBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
      if (isAtBottom || isAiResponse) el.scrollTop = el.scrollHeight;
    }
  }, [messages, typingUsers, loading, readMap, channelMemberCount, isInitialLoadRef]);

  const onFileChange = e => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    handleFileSelect(file);
  };

  // 입력 변경 핸들러: 컨텍스트 + @멘션 감지
  const onInputChange = e => {
    handleInputChange(e);
    const text = e.target.value;
    const cursorPos = e.target.selectionStart;
    const textBefore = text.slice(0, cursorPos);
    const match = textBefore.match(/@(\S*)$/);
    setMentionQuery(match ? match[1] : null);
    setMentionIdx(0);
  };

  // 키 이벤트 핸들러
  const onKeyDown = e => {
    if (mentionMatches.length > 0) {
      if (e.key === 'ArrowUp') { e.preventDefault(); setMentionIdx(i => (i - 1 + mentionMatches.length) % mentionMatches.length); return; }
      if (e.key === 'ArrowDown') { e.preventDefault(); setMentionIdx(i => (i + 1) % mentionMatches.length); return; }
      if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); insertMention(mentionMatches[mentionIdx]); return; }
      if (e.key === 'Escape') { setMentionQuery(null); return; }
    }
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      sendMessage();
    }
  };

  const memberNames = members.map(m => m.name);

  // AI 타이핑 vs 일반 타이핑 분리
  const aiTyping = !!typingUsers['__ai__'];
  const humanTypingList = Object.entries(typingUsers)
    .filter(([k]) => k !== '__ai__')
    .map(([, v]) => v);

  const canManageActive = activeChannel && (me?.role === 'superadmin' || activeChannel.createdBy === me?.id);

  if (!activeChannel) {
    return (
      <div className="flex-1 min-h-0 flex flex-col items-center justify-center text-gray-500 p-6">
        <svg className="w-12 h-12 mb-3 text-gray-700" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M8.625 12a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H8.25m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H12m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0h-.375M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 01-2.555-.337A5.972 5.972 0 015.41 20.97a5.969 5.969 0 01-.474-.065 4.48 4.48 0 00.978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25z" />
        </svg>
        <p className="text-sm text-center">채널을 선택하면<br />대화를 시작할 수 있습니다</p>
      </div>
    );
  }

  return (
    <div className="flex-1 min-h-0 flex flex-col bg-gray-900 relative">
      {/* 채널 헤더 */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-800 flex-shrink-0 bg-gray-900">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-white truncate">
            {activeChannel.type === 'dm'
              ? activeChannel.dmPartner?.name || activeChannel.name
              : `# ${activeChannel.name}`}
          </p>
          {activeChannel.description && <p className="text-xs text-gray-500 truncate">{activeChannel.description}</p>}
        </div>
        <div className="flex items-center gap-1.5 ml-3 flex-shrink-0">
          <button
            onClick={() => setShowMembers(v => !v)}
            className={`flex items-center gap-1 px-2.5 py-1.5 text-xs rounded-lg transition-colors ${showMembers ? 'bg-brand/20 text-brand' : 'text-gray-400 border border-gray-700 hover:bg-gray-800'}`}
          >
            <Users className="w-3.5 h-3.5" />
            {activeChannel.memberCount > 0 && <span>{activeChannel.memberCount}</span>}
          </button>
          {activeChannel.type !== 'dm' && (
            <button
              onClick={() => { setMeetingChannel(activeChannel); setMeetingOpen(true); }}
              className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-white bg-brand hover:bg-brand/80 rounded-lg transition-colors"
              title="화상회의 시작"
            >
              <Video className="w-3.5 h-3.5" />
              화상
            </button>
          )}
        </div>
      </div>

      {/* 메시지 목록 */}
      <div ref={scrollContainerRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-0.5">
        {loading && (
          <div className="flex justify-center py-6">
            <div className="w-5 h-5 border-2 border-brand/30 border-t-brand rounded-full animate-spin" />
          </div>
        )}
        {!loading && messages.map((msg, idx) => {
          const isAI = msg.type === 'ai';
          const isMine = !isAI && msg.userId === me?.id;
          const grouped = isSameGroup(messages[idx - 1] || null, msg);
          const msgTime = new Date(msg.createdAt).getTime();
          const readersCount = Object.values(readMap).filter(t => t && new Date(t).getTime() >= msgTime).length;
          const unreadCount = channelMemberCount > 1 ? Math.max(0, channelMemberCount - readersCount) : 0;

          return (
            <div
              key={msg.id}
              className={`flex gap-2.5 ${grouped ? '' : 'pt-2'} ${isMine ? 'flex-row-reverse' : ''}`}
              onMouseEnter={() => setHoverMsgId(msg.id)}
              onMouseLeave={() => { setHoverMsgId(null); if (emojiPickerMsgId === msg.id) setEmojiPickerMsgId(null); }}
            >
              {/* 아바타 */}
              {!isMine && (
                <div className="flex-shrink-0 w-9">
                  {!grouped && (isAI ? <AIAvatar /> : <Avatar user={msg.user} size={9} />)}
                </div>
              )}

              <div className={`min-w-0 max-w-[75%] ${isMine ? 'items-end' : 'items-start'} flex flex-col`}>
                {/* 이름 + 시간 */}
                {!grouped && (
                  <div className={`flex items-baseline gap-2 mb-1 ${isMine ? 'flex-row-reverse' : ''}`}>
                    {!isMine && (
                      <span className={`text-xs font-semibold ${isAI ? 'text-indigo-400' : 'text-white'}`}>
                        {isAI ? '프린만 AI' : (msg.user?.name || '-')}
                      </span>
                    )}
                    <span className="text-[10px] text-gray-500">{formatTime(msg.createdAt)}</span>
                    {msg.edited && <span className="text-[10px] text-gray-600">(수정됨)</span>}
                  </div>
                )}

                {/* 메시지 본문 + 액션 버튼 */}
                <div className="relative">
                  {/* 본문 */}
                  <div className="min-w-0">
                    {msg.deleted
                      ? <p className="text-xs text-gray-500 italic px-3 py-1.5">삭제된 메시지</p>
                      : editingId === msg.id
                        ? (
                          <InlineEdit
                            content={msg.content}
                            onSave={text => { editMessage(msg.id, text); setEditingId(null); }}
                            onCancel={() => setEditingId(null)}
                          />
                        )
                        : msg.type === 'file'
                          ? <FileMessage content={msg.content} isMine={isMine} onImageLoad={() => { const el = scrollContainerRef.current; if (el) el.scrollTop = el.scrollHeight; }} />
                          : (
                            <div
                              className={`px-3 py-2 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap break-words ${
                                isAI
                                  ? 'rounded-tl-sm'
                                  : isMine
                                    ? 'bg-brand text-white rounded-tr-sm'
                                    : 'bg-gray-800 text-gray-200 rounded-tl-sm'
                              }`}
                              style={isAI ? {
                                background: 'rgba(238,242,255,0.92)',
                                border: '1px solid rgba(99,102,241,0.3)',
                                color: '#1e1b4b',
                              } : undefined}
                            >
                              {isAI
                                ? renderMarkdown(msg.content)
                                : renderText(msg.content, memberNames, me?.name)}
                            </div>
                          )
                    }
                    {/* 이모지 리액션 */}
                    {!msg.deleted && (
                      <ReactionBar
                        reactions={msg.reactions}
                        myId={me?.id}
                        onReact={emoji => sendReaction(msg.id, emoji)}
                      />
                    )}
                  </div>

                  {/* 호버 액션 버튼 — absolute로 레이아웃 영향 없음 */}
                  {!msg.deleted && hoverMsgId === msg.id && (
                    <div className={`absolute top-0 flex items-center gap-0.5 z-10 ${isMine ? 'right-full mr-1' : 'left-full ml-1'}`}>
                      {/* 이모지 반응 버튼 */}
                      <div className="relative">
                        <button
                          onClick={() => setEmojiPickerMsgId(v => v === msg.id ? null : msg.id)}
                          className="w-7 h-7 flex items-center justify-center rounded-lg bg-gray-800 border border-gray-700 text-gray-400 hover:text-white hover:border-gray-500 transition-colors"
                          title="이모지 반응"
                        >
                          <Smile size={13} />
                        </button>
                        {emojiPickerMsgId === msg.id && (
                          <EmojiPickerPopup
                            onSelect={emoji => sendReaction(msg.id, emoji)}
                            onClose={() => setEmojiPickerMsgId(null)}
                            alignRight={isMine}
                          />
                        )}
                      </div>
                      {/* 편집 버튼 (내 메시지만, AI 메시지 제외) */}
                      {isMine && !isAI && (
                        <button
                          onClick={() => setEditingId(msg.id)}
                          className="w-7 h-7 flex items-center justify-center rounded-lg bg-gray-800 border border-gray-700 text-gray-400 hover:text-white hover:border-gray-500 transition-colors"
                          title="메시지 수정"
                        >
                          <Pencil size={12} />
                        </button>
                      )}
                    </div>
                  )}
                </div>
                {/* 미읽음 수 (카톡 스타일) */}
                {!msg.deleted && unreadCount > 0 && (
                  <span className={`text-[10px] font-bold text-yellow-400 leading-none mt-0.5 ${isMine ? 'self-start' : 'self-end'}`}>
                    {unreadCount}
                  </span>
                )}
              </div>
            </div>
          );
        })}

        {/* 타이핑 인디케이터 */}
        {(humanTypingList.length > 0 || aiTyping) && (
          <div className="flex flex-col gap-1 pl-1 pt-1">
            {aiTyping && (
              <div className="flex items-center gap-1.5 pt-2">
                <AIAvatar />
                <div className="rounded-2xl rounded-tl-sm px-3 py-2 inline-flex items-center" style={{ background: 'rgba(238,242,255,0.92)', border: '1px solid rgba(99,102,241,0.3)' }}>
                  <span className="text-[11px]" style={{ color: '#4338ca' }}>프린만 AI가 답변 생성 중</span>
                  <TypingDots />
                </div>
              </div>
            )}
            {humanTypingList.length > 0 && (
              <div className="flex items-center gap-1">
                <p className="text-[11px] text-gray-500">
                  {humanTypingList.slice(0, 2).join(', ')}{humanTypingList.length > 2 && ` 외 ${humanTypingList.length - 2}명`}님이 입력 중
                </p>
                <TypingDots />
              </div>
            )}
          </div>
        )}
        <div />
      </div>

      {/* 입력 영역 */}
      <div className="border-t border-gray-800 px-4 py-3 flex-shrink-0 bg-gray-900">
        <input ref={fileInputRef} type="file" className="hidden" onChange={onFileChange} />

        {/* @멘션 자동완성 드롭업 */}
        {mentionMatches.length > 0 && (
          <div className="mb-2 bg-gray-800 border border-gray-700 rounded-xl overflow-hidden">
            <p className="px-3 py-1.5 text-[10px] text-gray-500 border-b border-gray-700">멘션 보내기</p>
            {mentionMatches.map((entry, i) => (
              <button
                key={entry.id}
                onClick={() => insertMention(entry)}
                className={`w-full flex items-center gap-2.5 px-3 py-2 text-left text-sm transition-colors ${i === mentionIdx ? 'bg-gray-700 text-white' : 'text-gray-300 hover:bg-gray-700/60'}`}
              >
                {entry.isAI
                  ? <AIAvatar size={7} />
                  : <div className="w-7 h-7 rounded-full bg-brand/20 text-brand text-[10px] font-bold flex items-center justify-center flex-shrink-0">{getInitials(entry.name)}</div>
                }
                <div>
                  <span className="font-medium">{entry.isAI ? '프린만 AI' : entry.name}</span>
                  {entry.department && <span className="ml-1.5 text-xs text-gray-500">{entry.department}</span>}
                  {entry.isAI && <span className="ml-1.5 text-xs text-pink-500">@AI</span>}
                </div>
              </button>
            ))}
            <p className="px-3 py-1 text-[10px] text-gray-600">↑↓ 이동 · Enter 선택 · Esc 닫기</p>
          </div>
        )}

        <div className="flex items-end gap-2">
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="flex-shrink-0 w-9 h-9 flex items-center justify-center rounded-lg text-gray-500 hover:text-white hover:bg-gray-800 transition-colors disabled:opacity-40"
            title="파일 첨부"
          >
            {uploading
              ? <div className="w-4 h-4 border-2 border-brand/30 border-t-brand rounded-full animate-spin" />
              : <Paperclip size={16} />
            }
          </button>
          <textarea
            ref={textareaRef}
            rows={1}
            className="flex-1 resize-none rounded-lg border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-brand leading-5 min-h-[2.25rem] max-h-28 overflow-y-auto"
            placeholder={activeChannel.type === 'dm'
  ? `${activeChannel.dmPartner?.name || '상대방'}에게 메시지 보내기`
  : `#${activeChannel.name}에 메시지 보내기 · @로 멘션`}
            value={newMsg}
            onChange={onInputChange}
            onKeyDown={onKeyDown}
          />
          <button
            onClick={sendMessage}
            disabled={!newMsg.trim()}
            className="flex-shrink-0 w-9 h-9 flex items-center justify-center rounded-lg bg-brand text-white hover:bg-brand/80 disabled:opacity-40"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>

      {/* 멤버 관리 오버레이 */}
      {showMembers && (
        <MembersPanel
          channel={activeChannel}
          me={me}
          onClose={() => setShowMembers(false)}
          onMembersChanged={loadChannels}
        />
      )}
    </div>
  );
}
